import { LOADING_STATE } from "types/general";

export enum ResearchStaffLevelEnum {
  RESEARCHER = "RESEARCHER",
  REVIEWER_MANAGER = "REVIEWER_MANAGER",
}

export enum ResearchStageEnum {
  INTAKE_QUEUE = "INTAKE_QUEUE",
  ABOVE_BOARD_CONVERSATION = "ABOVE_BOARD_CONVERSATION",
  SHOP_QUEUE = "SHOP_QUEUE",
  SHOP_IN_PROCESS = "SHOP_IN_PROCESS",
  RETRY_QUEUE = "RETRY_QUEUE",
  RETRY_IN_PROCESS = "RETRY_IN_PROCESS",
  READY_FOR_REVIEW = "READY_FOR_REVIEW",
  REQUIRES_FIX = "REQUIRES_FIX",
  COMPLETED = "COMPLETED",
}

export enum ResearchWorkTypeEnum {
  TIME_SENSITIVE_REQUEST = "TIME_SENSITIVE_REQUEST",
  SCHEDULED_SHOP_ROUND = "SCHEDULED_SHOP_ROUND",
}

export enum ResearchCareTypeEnum {
  AA = "AA",
  IL = "IL",
  AL = "AL",
  MC = "MC",
  SN = "SN",
}

export enum ResearchSelectionRoleEnum {
  SUBJECT = "SUBJECT",
  COMPETITOR = "COMPETITOR",
  SINGLE_PROPERTY_OVERRIDE = "SINGLE_PROPERTY_OVERRIDE",
}

export enum ResearchPriorCycleActionEnum {
  CREATE_NEW_CYCLE = "CREATE_NEW_CYCLE",
  FULFILL_WITH_PRIOR_COMP = "FULFILL_WITH_PRIOR_COMP",
  REOPEN_PRIOR_CYCLE = "REOPEN_PRIOR_CYCLE",
}

export enum ResearchDemandStatusEnum {
  ACTIVE = "ACTIVE",
  CANCELLED = "CANCELLED",
  FULFILLED = "FULFILLED",
}

export enum ResearchClientProgressStatusEnum {
  ON_TRACK = "ON_TRACK",
  AT_RISK = "AT_RISK",
  DUE_TODAY = "DUE_TODAY",
  OVERDUE = "OVERDUE",
  COMPLETE = "COMPLETE",
}

export enum ResearchProductivityCategoryEnum {
  RESEARCH = "RESEARCH",
  REVIEW = "REVIEW",
}

export enum ResearchPhaseEnum {
  ABOVE_BOARD = "ABOVE_BOARD",
  FIRST_SHOP = "FIRST_SHOP",
  RETRY = "RETRY",
  REVIEW = "REVIEW",
}

export interface IResearchClient {
  id: string;
  name: string;
}

export interface IResearchStaff {
  id: string;
  firstName: string;
  lastName: string;
  level: ResearchStaffLevelEnum;
}

export interface IResearchReferenceData {
  clients: IResearchClient[];
  staff: IResearchStaff[];
  workTypes: ResearchWorkTypeEnum[];
  careTypes: ResearchCareTypeEnum[];
  stages: ResearchStageEnum[];
  manager: boolean;
}

export interface IResearchSummary {
  counts: Partial<Record<ResearchStageEnum, number>>;
  totalActive: number;
  transitions: IResearchTransitionCount[];
}

export interface IResearchTransitionCount {
  fromStage: ResearchStageEnum;
  toStage: ResearchStageEnum;
  count: number;
}

export interface IResearchPerson {
  id: string;
  firstName: string;
  lastName: string;
}

export interface IResearchProperty {
  id: string;
  sequentialId: number | null;
  name: string;
  city: string;
  state: string;
  operator: string;
}

export interface IResearchDemandContext {
  dealId: string;
  dealName: string;
  subjectPropertyId: string;
  subjectPropertyName: string;
}

export interface IResearchDemand {
  id: string;
  clientId: string;
  clientName: string;
  workType: ResearchWorkTypeEnum;
  dueDate: string;
  priorityCareTypes: ResearchCareTypeEnum[];
  status: ResearchDemandStatusEnum;
  contexts: IResearchDemandContext[];
}

export interface IResearchClientShare {
  clientId: string;
  clientName: string;
  shared: boolean;
}

export interface IResearchWorkingComp {
  id: string;
  sequentialId: number | null;
  status: string;
  available: boolean;
  requiredClientShares: IResearchClientShare[];
  allRequiredClientsShared: boolean;
}

export interface IResearchAdvisory {
  eventId: string;
  message: string;
  occurredAt: string;
}

export interface IResearchWorkItem {
  id: string;
  version: number;
  property: IResearchProperty;
  stage: ResearchStageEnum;
  active: boolean;
  assignedTo: IResearchPerson | null;
  submittedBy: IResearchPerson | null;
  workingComp: IResearchWorkingComp | null;
  demands: IResearchDemand[];
  allowedTransitions: ResearchStageEnum[];
  availableToCurrentUser: boolean;
  currentUserPreviouslyParticipated: boolean;
  lastActivityAt: string;
  daysInStage: number;
  advisories: IResearchAdvisory[];
  creationDate: string;
  completedAt: string | null;
  archivedAt: string | null;
  archiveReason: string | null;
}

export interface IResearchClientProgress {
  clientId: string;
  clientName: string;
  dueDate: string;
  workTypes: ResearchWorkTypeEnum[];
  total: number;
  started: number;
  completed: number;
  remaining: number;
  completionPercentage: number;
  workdaysRemaining: number;
  completionsPerWorkdayRequired: number | null;
  expectedCompletedByToday: number;
  status: ResearchClientProgressStatusEnum;
  stageCounts: Record<ResearchStageEnum, number>;
}

export interface IResearchWorkEvent {
  id: string;
  eventType: string;
  fromStage: ResearchStageEnum | null;
  toStage: ResearchStageEnum | null;
  actor: IResearchPerson | null;
  assignedTo: IResearchPerson | null;
  compId: string | null;
  reason: string | null;
  occurredAt: string;
}

export interface IResearchPropertyPath {
  workItem: IResearchWorkItem;
  stageVisits: IResearchStageVisit[];
  generatedAt: string;
  events: IResearchWorkEvent[];
}

export interface IResearchStageVisit {
  visitId: string;
  stage: ResearchStageEnum;
  enteredAt: string;
  exitedAt: string | null;
  durationMinutes: number;
  enteredBy: IResearchPerson | null;
  assignedTo: IResearchPerson | null;
}

export interface IResearchRecentComp {
  compId: string;
  verificationDate: string;
  ageDays: number;
  capturedCareTypes: ResearchCareTypeEnum[];
  compStatus: string;
  priorWorkItemId: string | null;
  priorDecision: string | null;
  sharedCompanyIds: string[];
  priorCycleChoiceRequired: boolean;
}

export interface IResearchIntakeCandidate {
  propertyId: string;
  propertyName: string;
  activeWorkItemId: string | null;
  activeStage: ResearchStageEnum | null;
  activeClientCount: number;
  priorityCareTypes: ResearchCareTypeEnum[];
  recentComp: IResearchRecentComp | null;
}

export interface IResearchIntakeExclusion {
  propertyId: string;
  propertyName: string;
  reason: string;
  sourceClientId: string | null;
  sourceClientName: string | null;
}

export interface IResearchSubjectAdvisory {
  propertyId: string;
  propertyName: string;
  activeWorkItemId: string;
  activeStage: ResearchStageEnum;
  message: string;
}

export interface IResearchIntakePreview {
  candidates: IResearchIntakeCandidate[];
  exclusions: IResearchIntakeExclusion[];
  subjectAdvisories: IResearchSubjectAdvisory[];
}

export interface IResearchIntakeSelection {
  propertyId: string;
  role: ResearchSelectionRoleEnum;
  dealId?: string;
  subjectPropertyId?: string;
  priorityCareTypes?: ResearchCareTypeEnum[];
}

export interface IResearchIntakeRequest {
  clientCompanyId: string;
  workType: ResearchWorkTypeEnum;
  dueDate: string;
  selections: IResearchIntakeSelection[];
  priorCycleActions?: Record<string, ResearchPriorCycleActionEnum>;
}

export interface IResearchIntakeResult {
  propertyId: string;
  propertyName: string;
  workItemId: string | null;
  stage: ResearchStageEnum | null;
  disposition: string;
}

export interface IResearchIntakeResponse {
  batchId: string;
  results: IResearchIntakeResult[];
}

export interface IResearchProductivityEntry {
  workItemId: string;
  propertyId: string;
  propertyName: string;
  category: ResearchProductivityCategoryEnum;
  phase: ResearchPhaseEnum;
  points: number;
  adjustmentReason: string | null;
}

export interface IResearchProductivityPerson {
  userId: string;
  firstName: string;
  lastName: string;
  total: number;
  starts: number;
  entries: IResearchProductivityEntry[];
}

export interface IResearchProductivityDay {
  date: string;
  people: IResearchProductivityPerson[];
  total: number;
}

export interface IResearchProductivity {
  from: string | null;
  to: string | null;
  days: IResearchProductivityDay[];
}

export interface IResearchWorkFilters {
  stage?: ResearchStageEnum;
  assignedUserId?: string;
  clientCompanyId?: string;
  workType?: ResearchWorkTypeEnum;
  dueFrom?: string;
  dueTo?: string;
  availableToMe?: boolean;
  priorParticipantUserId?: string;
  overdueOnly?: boolean;
  archivedOnly?: boolean;
  search?: string;
}

export type IResearchSummaryFilters = Omit<
  IResearchWorkFilters,
  "stage" | "dueFrom" | "dueTo"
> & {
  activityFrom?: string;
  activityTo?: string;
};

export interface IResearchCompContext {
  workItemId: string;
  version: number;
  compId?: string;
  mode: "choose" | "create" | "edit" | "view";
}

export interface IResearchState {
  referenceData: {
    data?: IResearchReferenceData;
    loadingState: LOADING_STATE;
  };
  summary: {
    data?: IResearchSummary;
    loadingState: LOADING_STATE;
  };
  workItems: {
    data: IResearchWorkItem[];
    loadingState: LOADING_STATE;
  };
  clientProgress: {
    data: IResearchClientProgress[];
    loadingState: LOADING_STATE;
  };
  selectedPath: {
    data?: IResearchPropertyPath;
    loadingState: LOADING_STATE;
  };
  propertyHistory: {
    data: IResearchPropertyPath[];
    loadingState: LOADING_STATE;
  };
  productivity: {
    data?: IResearchProductivity;
    loadingState: LOADING_STATE;
  };
  intake: {
    preview?: IResearchIntakePreview;
    loadingState: LOADING_STATE;
  };
  compContext?: IResearchCompContext;
}
